// Main application entry point

import './styles/index.css';
import { getUrlParam, setUrlParam } from './utils/helpers';
// Lazy-loaded scenario modules (dynamic imports for code splitting)
import type { BakeOffView } from './components/pages/BakeOffView';
import type { ContentSteering } from './components/pages/ContentSteering';
import { ThemeToggle } from './components/ui/ThemeToggle';
import { themeManager } from './services/themeManager'; // Initialize theme on load
import { injectSpeedInsights } from '@vercel/speed-insights';
import { inject } from '@vercel/analytics';
import { VercelAnalytics } from './services/vercelAnalytics';
import { isElectron } from './services/ElectronBridge';
import { createLogger } from './utils/logger';
const log = createLogger('App');

// Initialize Vercel monitoring (web only)
if (typeof window !== 'undefined' && !window.location.protocol.startsWith('file')) {
  injectSpeedInsights();
  inject();
}

// Initialize Electron header capture bridge if running in Electron
if (isElectron()) {
  log.log('Running in Electron - enabling header capture bridge');
  // Dynamic import to prevent bundling in webapp
  import('./services/ElectronBridge').then(({ ElectronBridge }) => {
    ElectronBridge.getInstance().init();
  });
  // Mount auto-update notification banner
  import('./components/ui/UpdateNotificationBanner').then(({ UpdateNotificationBanner }) => {
    new UpdateNotificationBanner();
  });
}


// Track current scenario
let currentScenario: 'a' | 'b' = 'b';
let currentView: BakeOffView | ContentSteering | null = null;

/**
 * Initialize the application
 */
function init() {
  log.log('Application starting...');

  // Check for scenario in URL
  const scenarioParam = getUrlParam('scenario');
  if (scenarioParam === 'a' || scenarioParam === 'b') {
    currentScenario = scenarioParam;
  }

  // Set up navigation
  setupNavigation();

  // Set up tools dropdown
  setupToolsButton();

  // Set up global tooltips
  setupTooltips();

  // Set up keyboard shortcuts
  setupKeyboardShortcuts();

  // Set up theme toggle
  setupThemeToggle();

  // Display version
  displayVersion();

  // Set header height CSS variable (used for scenario-container height calc)
  const setHeaderHeight = () => {
    const header = document.querySelector<HTMLElement>('.app-header');
    if (header) {
      document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
    }
  };
  setHeaderHeight();
  window.addEventListener('resize', setHeaderHeight);

  // Load initial scenario
  loadScenario(currentScenario);

  log.log('Application initialized');
}

/**
 * Set up scenario navigation
 */
function setupNavigation() {
  const btnScenarioA = document.getElementById('btn-scenario-a');
  const btnScenarioB = document.getElementById('btn-scenario-b');

  if (!btnScenarioA || !btnScenarioB) {
    log.error('Navigation buttons not found');
    return;
  }

  // Scenario A button
  btnScenarioA.addEventListener('click', () => {
    if (currentScenario !== 'a') {
      switchScenario('a');
    }
  });

  // Scenario B button
  btnScenarioB.addEventListener('click', () => {
    if (currentScenario !== 'b') {
      switchScenario('b');
    }
  });

  // Update active state
  updateNavigationState();
}

/**
 * Set up tools dropdown button
 */
function setupToolsButton() {
  const btn = document.getElementById('btn-tools');
  const dropdown = document.getElementById('tools-dropdown');
  if (!btn || !dropdown) return;

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    const isOpen = !dropdown.hidden;
    dropdown.hidden = isOpen;
    btn.setAttribute('aria-expanded', String(!isOpen));
  });

  document.addEventListener('click', () => {
    if (!dropdown.hidden) {
      dropdown.hidden = true;
      btn.setAttribute('aria-expanded', 'false');
    }
  });

  // Hide the download link and its divider when already running inside Electron.
  // Note: cannot use the `hidden` attribute here — `.tools-dropdown-item { display: flex }`
  // has higher CSS specificity and overrides the UA [hidden] rule.
  if (isElectron()) {
    const downloadLink = document.getElementById('download-electron-link');
    if (downloadLink) {
      downloadLink.style.display = 'none';
      const divider = downloadLink.previousElementSibling as HTMLElement | null;
      if (divider?.classList.contains('tools-dropdown-divider')) divider.style.display = 'none';
    }
  }
}

/**
 * Set up global tooltips via a single body-level element (avoids z-index/overflow clipping)
 */
function setupTooltips() {
  const tooltip = document.createElement('div');
  tooltip.className = 'app-tooltip';
  document.body.appendChild(tooltip);

  let showTimer: ReturnType<typeof setTimeout> | null = null;

  document.addEventListener('mouseover', (e) => {
    const target = (e.target as HTMLElement).closest('[data-tooltip]') as HTMLElement | null;
    if (!target) return;
    const text = target.getAttribute('data-tooltip');
    if (!text) return;

    if (showTimer) clearTimeout(showTimer);
    tooltip.classList.remove('is-visible');

    showTimer = setTimeout(() => {
      tooltip.textContent = text;
      requestAnimationFrame(() => {
        const rect = target.getBoundingClientRect();
        const tipRect = tooltip.getBoundingClientRect();
        const left = Math.max(8, rect.left + rect.width / 2 - tipRect.width / 2);
        const top = Math.max(8, rect.top - tipRect.height - 8);
        tooltip.style.left = `${left}px`;
        tooltip.style.top = `${top}px`;
        tooltip.classList.add('is-visible');
      });
    }, 1000);
  });

  document.addEventListener('mouseout', (e) => {
    const target = (e.target as HTMLElement).closest('[data-tooltip]') as HTMLElement | null;
    if (!target) return;
    const related = e.relatedTarget as HTMLElement | null;
    if (related && target.contains(related)) return;
    if (showTimer) { clearTimeout(showTimer); showTimer = null; }
    tooltip.classList.remove('is-visible');
  });
}

/**
 * Set up global keyboard shortcuts
 */
function setupKeyboardShortcuts() {
  document.addEventListener('keydown', (e) => {
    // Ignore input fields
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      return;
    }

    // 't' or 'T' to toggle theme
    if (e.key.toLowerCase() === 't') {
      themeManager.toggleTheme();
    }
  });
}

/**
 * Set up theme toggle button
 */
function setupThemeToggle() {
  const actionsContainer = document.querySelector('.header-actions');
  if (!actionsContainer) {
    log.error('Header actions container not found');
    return;
  }

  const themeToggle = new ThemeToggle();
  actionsContainer.appendChild(themeToggle.getElement());
}

/**
 * Display app version in header pill
 */
function displayVersion() {
  const numberEl = document.getElementById('app-version-number');
  if (numberEl) {
    numberEl.textContent = '';
    numberEl.dataset.version = `v${APP_VERSION}`;
    numberEl.dataset.commit = `#${APP_COMMIT}`;
  }
  const metaEl = document.querySelector<HTMLMetaElement>('meta[name="build-commit"]');
  if (metaEl) metaEl.content = APP_COMMIT;
}

/**
 * Switch to a different scenario
 * @param scenario - Scenario to switch to
 */
async function switchScenario(scenario: 'a' | 'b') {
  log.log(`Switching to scenario ${scenario.toUpperCase()}`);

  // Cleanup current view
  if (currentView) {
    currentView.destroy();
    currentView = null;
  }

  currentScenario = scenario;
  setUrlParam('scenario', scenario);
  updateNavigationState();
  await loadScenario(scenario);
}

/**
 * Update navigation button states
 */
function updateNavigationState() {
  const navButtons = document.querySelectorAll('.nav-btn');
  navButtons.forEach(btn => {
    const btnScenario = btn.getAttribute('data-scenario');
    if (btnScenario === currentScenario) {
      btn.classList.add('active');
    } else {
      btn.classList.remove('active');
    }
  });
}

/**
 * Load and render a scenario
 * @param scenario - Scenario to load
 */
async function loadScenario(scenario: 'a' | 'b') {
  const mainContent = document.getElementById('main-content');
  if (!mainContent) {
    log.error('Main content container not found');
    return;
  }

  // Clear current content
  mainContent.innerHTML = '';

  // Track scenario switch
  VercelAnalytics.trackScenarioSwitch(scenario);

  if (scenario === 'a') {
    await loadScenarioA(mainContent);
  } else {
    await loadScenarioB(mainContent);
  }
}

/**
 * Load Scenario A: Split-Screen QoE Lab (Lazy-loaded)
 * @param container - Container element
 */
async function loadScenarioA(container: HTMLElement) {
  log.log('Lazy-loading split-screen view...');

  // Dynamic import for code splitting
  const { BakeOffView } = await import('./components/pages/BakeOffView');
  currentView = new BakeOffView(container);
  await currentView.init();
}

/**
 * Load Scenario B: Single Player with Content Steering (Lazy-loaded)
 * @param container - Container element
 */
async function loadScenarioB(container: HTMLElement) {
  log.log('Lazy-loading content steering...');

  // Dynamic import for code splitting
  const { ContentSteering } = await import('./components/pages/ContentSteering');
  currentView = new ContentSteering(container);
  await currentView.init();
}

// Start the application when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
